// src/components/DashboardView.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plane, PlaneLanding, PlaneTakeoff, Fuel, DollarSign, Warehouse, 
  Clock, Navigation, Gauge, Wifi, WifiOff
} from 'lucide-react';
import MapView from './MapView';
import VisitorsList from './visitors/VisitorsList';
import AlertWidget from '../components/AlertWidget';
import UpcomingEventsWidget from './UpcomingEventsWidget';
import LiveAlertsWidget from './LiveAlertsWidget';
import { externalApi } from '../contexts/authClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const StatCard = ({ title, value, icon: Icon, color, suffix = '' }) => (
  <div className="bg-gray-800/50 rounded-xl p-3 sm:p-5 border border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-xs sm:text-sm">{title}</p>
        <p className="text-lg sm:text-2xl font-bold text-white mt-1">
          {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
        </p>
      </div>
      <div className={`p-2 sm:p-3 rounded-xl bg-${color}-500/20`}>
        <Icon className={`w-4 h-4 sm:w-6 sm:h-6 text-${color}-400`} />
      </div>
    </div>
  </div>
);

const DashboardView = () => {
  const [stats, setStats] = useState({
    totalFlights: 0, arrivals: 0, departures: 0,
    fuelSold: 0, revenue: 0, activeLeases: 0
  });
  const [liveFlights, setLiveFlights] = useState([]);
  const [recentFlights, setRecentFlights] = useState([]);
  const [airport, setAirport] = useState(null);
  const [companyTimezone, setCompanyTimezone] = useState('America/Chicago');
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [trackingActive, setTrackingActive] = useState(true);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [trackingError, setTrackingError] = useState(false);
  const [refreshVisitors, setRefreshVisitors] = useState(0);

  // Obtenir les coordonnées via l'API Open-Meteo Geocoding
  const getAirportCoordinates = async (icaoCode) => {
    try {
      const response = await externalApi.get('https://geocoding-api.open-meteo.com/v1/search', {
        params: {
          name: icaoCode,
          count: 1,
          language: 'en',
          format: 'json'
        }
      });
      
      if (response.data.results && response.data.results.length > 0) {
        return {
          latitude: response.data.results[0].latitude,
          longitude: response.data.results[0].longitude,
          name: response.data.results[0].name
        };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    
    return null;
  };

  // Charger les settings de l'aéroport depuis la base de données
  useEffect(() => {
    const fetchAirportSettings = async () => {
      try {
        const res = await axios.get(`${API_BASE}/company/settings`);
        
        if (res.data) {
          const timezone = res.data.timezone || 'America/Chicago';
          setCompanyTimezone(timezone);
          
          let airportData = {
            icao_code: res.data.icao_code || 'KBOE',
            name: res.data.name || 'Airport',
            latitude: res.data.latitude,
            longitude: res.data.longitude,
            iata_code: res.data.iata_code,
            timezone: timezone,
            address: res.data.address
          };
          
          if (!airportData.latitude || !airportData.longitude) {
            const coords = await getAirportCoordinates(airportData.icao_code);
            if (coords) {
              airportData.latitude = coords.latitude;
              airportData.longitude = coords.longitude;
            } else {
              airportData.latitude = 29.7947;
              airportData.longitude = -98.7318;
            }
          } else {
            airportData.latitude = parseFloat(airportData.latitude);
            airportData.longitude = parseFloat(airportData.longitude);
          }
          
          setAirport(airportData);
        } else {
          setAirport({
            icao_code: 'KBOE',
            name: 'Boerne Stage Field',
            latitude: 29.7947,
            longitude: -98.7318,
            timezone: 'America/Chicago'
          });
        }
      } catch (e) {
        console.error('Error loading airport:', e);
        setAirport({
          icao_code: 'KBOE',
          name: 'Boerne Stage Field',
          latitude: 29.7947,
          longitude: -98.7318,
          timezone: 'America/Chicago'
        });
      }
    };
    
    fetchAirportSettings();
  }, []);

  // TRACKING LIVE - Récupération des vols en temps réel
  const fetchLiveTracking = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/tracking/live`, { timeout: 20000 }); // ✅ 20s au lieu de 10s
      const liveData = response.data || [];
      
      if (liveData.length > 0 || trackingError) {
        console.log(`[Tracking] ${liveData.length} aircraft detected`);
        setTrackingError(false);
      }
      
      // ✅ Réactiver le tracking si une requête réussit après une erreur
      if (!trackingActive) setTrackingActive(true);
      
      const formattedFlights = liveData.map(flight => ({
        id: flight.icao24 || flight.id,
        icao24: flight.icao24,
        tail_number: flight.tail_number || flight.callsign || 'N/A',
        callsign: flight.callsign,
        latitude: flight.latitude,
        longitude: flight.longitude,
        altitude: flight.altitude,
        velocity: flight.velocity,
        heading: flight.heading,
        on_ground: flight.on_ground,
        flight_type: flight.on_ground ? 'arrival' : 'departure',
        owner_name: flight.owner_name,
        timestamp: flight.timestamp
      }));
      
      setLiveFlights(formattedFlights);
    } catch (err) {
      console.error('Tracking API error:', err);
      setTrackingError(true);
      // ✅ Ne plus tuer trackingActive — on continue à réessayer automatiquement
      // setTrackingActive(false) supprimé : le polling continue même si OpenSky est temporairement down
    }
  }, [trackingActive, trackingError]);

  // Météo
  useEffect(() => {
    if (!airport?.latitude || !airport?.longitude) return;

    const fetchWeather = async () => {
      setWeatherLoading(true);
      try {
        const response = await externalApi.get('https://api.open-meteo.com/v1/forecast', {
          params: {
            latitude: airport.latitude,
            longitude: airport.longitude,
            current: 'temperature_2m,relative_humidity_2m,weathercode,windspeed_10m,winddirection_10m,pressure_msl',
            temperature_unit: 'fahrenheit',
            wind_speed_unit: 'kn',
            timezone: companyTimezone,
            forecast_days: 1,
          }
        });

        const data = response.data.current;
        setWeather({
          temperature: Math.round(data.temperature_2m),
          humidity: data.relative_humidity_2m,
          condition: data.weathercode,
          windSpeed: Math.round(data.windspeed_10m),
          windDirection: data.winddirection_10m,
          pressure: Math.round(data.pressure_msl),
        });
      } catch (error) {
        console.error('Weather fetch error:', error);
        setWeather(null);
      } finally {
        setWeatherLoading(false);
      }
    };

    fetchWeather();
    const weatherInterval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(weatherInterval);
  }, [airport?.latitude, airport?.longitude, companyTimezone]);

  // Charger les données dashboard
  const fetchDashboardData = useCallback(async () => {
    if (!airport) return;
    
    try {
      const response = await axios.get(`${API_BASE}/dashboard/stats`);
      const data = response.data;
      
      if (data.error) {
        console.warn('Dashboard warning:', data.error);
      }
      
      setStats({
        totalFlights: data.stats?.totalFlights || 0,
        arrivals: data.stats?.arrivals || 0,
        departures: data.stats?.departures || 0,
        fuelSold: data.stats?.fuelSold || 0,
        revenue: data.stats?.revenue || 0,
        activeLeases: data.stats?.activeLeases || 0
      });
      
      // Formater les mouvements récents (limité à 5)
      const formattedMovements = (data.recentMovements || []).slice(0, 5).map(m => ({
        id: `${m.source}_${m.id}`,
        tail_number: m.tail_number,
        movement_type: m.movement_type,
        timestamp: m.timestamp,
        source: m.source
      }));
      
      setRecentFlights(formattedMovements);
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setLoading(false);
    }
  }, [airport]);

  // Initial load et intervalle pour dashboard
  useEffect(() => {
    if (!airport) return;
    
    fetchDashboardData();
    const dashboardInterval = setInterval(fetchDashboardData, 30000);
    
    return () => clearInterval(dashboardInterval);
  }, [airport, fetchDashboardData]);

  // Lancer le tracking live
  useEffect(() => {
    if (!airport) return;
    
    fetchLiveTracking();
    const trackingInterval = setInterval(fetchLiveTracking, 60000);
    
    return () => {
      clearInterval(trackingInterval);
    };
  }, [airport, fetchLiveTracking]);

  const getWeatherLabel = (code) => {
    if (code === 0) return 'Clear Sky';
    if (code === 1 || code === 2) return 'Mainly Clear';
    if (code === 3) return 'Overcast';
    if (code === 45 || code === 48) return 'Fog';
    if (code === 51 || code === 53 || code === 55) return 'Drizzle';
    if (code >= 61 && code <= 67) return 'Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Rain Showers';
    if (code >= 95 && code <= 99) return 'Thunderstorm';
    return 'Unknown';
  };

  const getWindDirection = (degrees) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  const handleVisitorConverted = () => {
    setRefreshVisitors(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 sm:h-96">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm sm:text-base">Loading dashboard data from your airport...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Quick Stats — "Revenue Today" remplacé par LiveAlertsWidget */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Total Flights Today" value={stats.totalFlights} icon={Plane}     color="blue"   />
        <StatCard title="Fuel Sold (Gal)"     value={stats.fuelSold}    icon={Fuel}      color="orange" suffix=" gal" />
        <StatCard title="Active Leases"       value={stats.activeLeases} icon={Warehouse} color="purple" />

        {/* ── Live Alerts widget — same grid cell as Revenue Today ── */}
        <LiveAlertsWidget />
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        
        {/* LEFT COLUMN - Map + Upcoming Events Widget */}
        <div className="flex-1 lg:flex-[2] space-y-4">
          {/* Map */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden" style={{ height: 'auto', minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
            <div className="p-2 sm:p-3 border-b border-gray-700 flex justify-between items-center flex-shrink-0">
              <h3 className="text-white font-semibold flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <Navigation size={14} className="sm:w-4 sm:h-4 text-blue-400" />
                Live Traffic — {airport?.icao_code || 'Airport'} Area
                {!trackingError && liveFlights.length > 0 && (
                  <span className="text-[10px] sm:text-xs text-green-400 ml-1 sm:ml-2 flex items-center gap-1">
                    <Wifi size={8} className="sm:w-2.5 sm:h-2.5" /> {liveFlights.length} aircraft
                  </span>
                )}
                {trackingError && (
                  <span className="text-[10px] sm:text-xs text-red-400 ml-1 sm:ml-2 flex items-center gap-1">
                    <WifiOff size={8} className="sm:w-2.5 sm:h-2.5" /> Tracking unavailable
                  </span>
                )}
              </h3>
            </div>
            <div className="relative overflow-hidden" style={{ height: '350px', minHeight: 0 }}>
              {airport ? (
                <MapView
                  flights={liveFlights}
                  airport={airport}
                  selectedFlight={selectedFlight}
                  onSelectFlight={setSelectedFlight}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  Loading map...
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Events Widget */}
          <UpcomingEventsWidget refreshTrigger={refreshVisitors} />
        </div>

        {/* RIGHT COLUMN - Weather, Recent Activity, Visitors */}
        <div className="flex-1 lg:flex-[1] space-y-4">
          {/* Weather Widget */}
          <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
            <h3 className="text-white font-semibold flex items-center gap-2 mb-2 sm:mb-3 text-xs sm:text-sm">
              <Gauge size={14} className="sm:w-4 sm:h-4 text-blue-400" />
              Weather at {airport?.icao_code || 'Airport'}
            </h3>

            {weatherLoading && (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-500"></div>
              </div>
            )}

            {!weatherLoading && weather && (
              <div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold text-white">{weather.temperature}°F</p>
                    <p className="text-xs text-gray-400">{getWeatherLabel(weather.condition)}</p>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Wind:</span>
                      <span className="text-gray-300">{weather.windSpeed} kts {getWindDirection(weather.windDirection)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Humidity:</span>
                      <span className="text-gray-300">{weather.humidity}%</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-right mt-2">
                  {new Date().toLocaleTimeString()}
                </p>
              </div>
            )}

            {!weatherLoading && !weather && (
              <div className="text-center text-gray-500 text-xs py-6">
                Weather data unavailable
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
            <h3 className="text-white font-semibold flex items-center gap-2 mb-2 sm:mb-3 text-xs sm:text-sm">
              <Clock size={14} className="sm:w-4 sm:h-4 text-blue-400" />
              Recent Activity
            </h3>
            <div className="space-y-2 max-h-[160px] sm:max-h-[180px] overflow-y-auto">
              {recentFlights.map((flight, idx) => (
                <div key={flight.id || idx} className="flex items-center justify-between text-xs rounded-lg px-2 py-1 hover:bg-gray-700/50">
                  <div className="flex items-center gap-2">
                    {flight.movement_type?.toLowerCase() === 'arrival' || flight.movement_type?.toLowerCase() === 'landing' ? (
                      <PlaneLanding size={10} className="sm:w-3 sm:h-3 text-green-500" />
                    ) : (
                      <PlaneTakeoff size={10} className="sm:w-3 sm:h-3 text-orange-500" />
                    )}
                    <span className="text-white text-xs">{flight.tail_number || 'N/A'}</span>
                  </div>
                  <span className="text-gray-400 text-[10px] sm:text-xs">
                    {flight.timestamp ? new Date(flight.timestamp).toLocaleTimeString() : '--:--'}
                  </span>
                </div>
              ))}
              {recentFlights.length === 0 && (
                <p className="text-gray-500 text-xs text-center py-4">
                  No flights yet.
                </p>
              )}
            </div>
          </div>

          {/* Visitors List */}
          <VisitorsList 
            onConvert={handleVisitorConverted}
            refreshTrigger={refreshVisitors}
          />
        </div>
      </div>

      {/* Welcome message */}
      {stats.totalFlights === 0 && stats.activeLeases === 0 && stats.fuelSold === 0 && (
        <div className="bg-blue-500/10 rounded-xl p-3 border border-blue-500/30">
          <p className="text-blue-400 text-xs text-center">
            ✈️ Welcome to {airport?.name || 'aeroTrick FBO'}! Start by adding flights, fuel transactions, or leases from the sidebar menu.
          </p>
        </div>
      )}

      {/* Alert Widget — intact, untouched */}
      <AlertWidget />
    </div>
  );
};

export default DashboardView;